import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { nextSession, TIME_LABEL } from "@/data/webinarSessions";
import { track } from "@/lib/analytics";

/**
 * The next CLARITY Briefing session, named.
 *
 * "Register for Free" on its own does not say what someone would be
 * registering for. This does: the series, that it is live, when it runs, and
 * the title of the session actually coming up. The session is read from the
 * published schedule, so it advances itself every week and can never sit
 * there advertising a date that has passed.
 *
 * `actionLabel` folds the registration button into this block. A separate
 * button above a card that says the same thing was two asks for one action
 * (the founder's call), so the whole block is the button: one border, one
 * click target, the action stated last where the eye lands.
 *
 * Renders nothing once the published schedule runs out, which is the honest
 * failure mode: better an absent strip than a stale one.
 */
export const NextSessionStrip = ({ source, className = "", actionLabel, actionTestId }) => {
  const session = nextSession();
  if (!session) return null;

  return (
    <Link
      to="/webinar"
      data-testid={`next-session-strip-${source}`}
      onClick={() => track("webinar_cta_click", { source: `${source}-strip` })}
      className={`group block border border-ink bg-paper transition-colors duration-200 hover:bg-ink/[0.03] ${className}`}
    >
      <div className="px-4 py-3.5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-blueprint">
          <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-blueprint opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blueprint" />
          </span>
          The CLARITY Briefing · Live
        </div>
        <div className="mt-2 grid grid-cols-[4rem_1fr] gap-3 items-start">
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink/60 pt-0.5">
            {session.label}
            {session.dayNote && (
              <div className="mt-0.5 text-[9px] tracking-[0.14em] text-blueprint">{session.dayNote}</div>
            )}
          </div>
          <div className="font-mono text-[13px] leading-[1.5] text-ink">{session.title}</div>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/60">
          Thursdays · {TIME_LABEL}
          {!actionLabel && (
            <ArrowRight
              size={12}
              strokeWidth={1.75}
              className="text-blueprint transition-transform duration-200 group-hover:translate-x-0.5"
            />
          )}
        </div>
      </div>
      {actionLabel && (
        <div
          data-testid={actionTestId}
          className="flex items-center justify-between gap-3 border-t border-ink bg-ink px-4 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-200 group-hover:bg-blueprint"
        >
          {actionLabel}
          <ArrowRight
            size={14}
            strokeWidth={1.75}
            className="transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </div>
      )}
    </Link>
  );
};

export default NextSessionStrip;
